import DBOperationsComb from "src/utils/dbOperationsComb";
import Constants from "src/utils/constants";
import Exception from "src/utils/exception";

const tableName = Constants.enums.TABLES_NAME_ENUM.DYNAMIC_FROMS_TABLE;

// The stored schemas are JSON strings; callers get them parsed.
function parseFormSchemas(row) {
  row.form_schema = JSON.parse(row.form_schema);
  row.grid_schema_data = JSON.parse(row.grid_schema_data);
  return row;
}

// A lookup that must match exactly one form — no match, or several, is an
// error rather than a guess.
function singleRow(res) {
  if (res?.error) return { row: null, error: res.error };
  const rows = res?.data ?? [];
  if (rows.length !== 1) {
    return {
      row: null,
      error: `Expected exactly one form, found ${rows.length}`
    };
  }
  return { row: rows[0], error: null };
}

async function createNewForm(data) {
  try {
    return await DBOperationsComb.insertData(tableName, data, false, {
      attribution: false
    });
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function updateForm(data) {
  try {
    let col_list = [];

    // Iterate through the schema object
    for (const key in JSON.parse(data.form_schema).schema) {
      if (
        Object.prototype.hasOwnProperty.call(
          JSON.parse(data.form_schema).schema,
          key
        )
      ) {
        if (!JSON.parse(data.form_schema).schema[key].dbCol.includes("_btn_")) {
          col_list.push(JSON.parse(data.form_schema).schema[key].dbCol);
        }
      }
    }
    // Create the submissions table (or add missing columns), then its RLS.
    await DBOperationsComb.checkAndCreateTable({
      tableName: data.table_name,
      columnNames: col_list
    });
    await DBOperationsComb.setupRlsForAuthenticatedUsers({
      tableName: data.table_name
    });
    return await DBOperationsComb.updateData(
      tableName,
      removeKeysWithObj(data),
      false,
      { attribution: false, syncStamp: false }
    );
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}
function removeKeysWithObj(data) {
  Object.keys(data).forEach((key) => {
    if (key.includes("_obj")) {
      delete data[key];
    }
  });

  // Return the updated object
  return data;
}
async function deleteForm(id) {
  try {
    let data = {
      id,
      is_deleted: true
    };
    let _res = await DBOperationsComb.updateData(tableName, data, false, {
      attribution: false,
      syncStamp: false
    });
    return { ..._res, data };
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}
async function recoverForm(id) {
  try {
    let data = {
      id,
      is_deleted: false
    };
    let _res = await DBOperationsComb.updateData(tableName, data, false, {
      attribution: false,
      syncStamp: false
    });
    return { ..._res, data };
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}
async function getForm(id) {
  try {
    const res = await DBOperationsComb.getData(tableName, id, false);
    if (res?.error || !res?.data) {
      return { error: res?.error ?? "Form not found", data: null };
    }
    return { error: null, data: parseFormSchemas(res.data) };
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}
async function getFormByTag(tag) {
  try {
    const tagsTable = Constants.enums.TABLES_NAME_ENUM.TAGS_TABLE;
    const { row, error } = singleRow(
      await DBOperationsComb.fetchData(
        {
          tableName,
          select: `*, ${tagsTable}!inner(tag)`,
          queryParams: [{ field: `${tagsTable}.tag`, value: tag }],
          fetchDeleted: true,
          fetchAll: true
        },
        false
      )
    );
    if (error) return { error, data: null };
    return {
      error: null,
      data: {
        schema: JSON.parse(row.form_schema),
        table_name: row.table_name
      }
    };
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}
async function getFormBySlug(slug) {
  try {
    const { row, error } = singleRow(
      await DBOperationsComb.fetchData(
        {
          tableName,
          queryParams: [{ field: "url_slug", value: slug }],
          fetchDeleted: true,
          fetchAll: true
        },
        false
      )
    );
    if (error) return { error, data: null };
    return { error: null, data: parseFormSchemas(row) };
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}
async function getForms(config) {
  try {
    const tagsTable = Constants.enums.TABLES_NAME_ENUM.TAGS_TABLE;
    return await DBOperationsComb.fetchData(
      {
        tableName,
        select: `id, created_at, updated_at, tag, notes, url_slug, is_deleted, tag_obj:${tagsTable} (id, tag, notes)`,
        fetchDeleted: config.fetchDeleted,
        searchTxt: config.searchTxt,
        // Newest first unless ascending is asked for, as this list always was.
        sortField: config.sortField ?? "created_at",
        sortOrder: config.sortOrder === 1 ? 1 : -1,
        page: config.page,
        rows: config.rows
      },
      false
    );
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}
async function formSubmision(tableName, data) {
  try {
    // Submitting form
    return await DBOperationsComb.insertData(tableName, data, false, {
      attribution: false
    });
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getSubmittedFormById(tableName, id) {
  try {
    return await DBOperationsComb.getData(tableName, id, false);
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

export default {
  createNewForm,
  updateForm,
  deleteForm,
  recoverForm,
  getForm,
  getFormByTag,
  getFormBySlug,
  getForms,
  formSubmision,
  getSubmittedFormById
};
